
import FeedbackModel from '@/Pages/CancelSubscriptionPage/FeedbackModel';
import { trackCancelSubscription } from '@/Services/Snowplow/SnowplowService';
import { SnowplowCancellationReasonIdentifier } from '@/Services/Snowplow/models';
import StripeSubscriptionService from '@/Services/Subscription/StripeSubscriptionService';
import EmptyState from '@/components/EmptyState.vue';
import Icon from '@/components/Icons/Icon.vue';
import QrCheckbox from '@/components/Input/QrCheckbox.vue';
import store from '@/store';
import type { Route } from '@sentry/vue/types/router';
import dayjs from 'dayjs';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { NavigationGuardNext } from 'vue-router';
import CounterArgumentBanner from '@/components/CancellationForm/CounterArgumentBanner.vue';
import { TranslateResult } from 'vue-i18n';
import DiscountOfferModal from '@/components/CancellationForm/DiscountOfferModal.vue';
import { optimizelyFeatureFlags } from '@/plugins/optimizely-feature-flags';
import { isFeatureEnabled } from '@/Services/FeatureFlagService';
import PromoCodeService, { DiscountInfo } from '@/Services/PromoCodeService';

interface CancellationReasonsInterface {
  id: number;
  label: TranslateResult;
  value: string;
  counterArgumentBanner?: {
    backgroundColor: string;
    title: string;
    description?: string;
    iconName: string;
    actions?: Array<{
      id: number;
      buttonColor: 'primary' | 'banana darken-1';
      buttonOutlined?: boolean;
      buttonCta: string;
      buttonLink: string;
    }>;
  };
}

@Component({
  components: {
    CounterArgumentBanner,
    Icon,
    EmptyState,
    QrCheckbox,
    DiscountOfferModal,
  },
})
export default class CancelSubscription extends Vue {
  // DATA
  form: FeedbackModel = new FeedbackModel();
  password = '';
  showPassword = false;
  questionFourSelected: any = [];
  answerFour: Array<TranslateResult> = [
    this.$t('fields.account.billing.cancel_subs.page_check_one'),
    this.$t('fields.account.billing.cancel_subs.page_check_two'),
    this.$t('fields.account.billing.cancel_subs.page_check_three'),
    this.$t('fields.account.billing.cancel_subs.page_check_four'),
  ];

  subscription = store.state.subscription.activeSubscription;
  stripeSubscriptionService: StripeSubscriptionService = new StripeSubscriptionService();

  // Loading states
  isUnsubscribing = false;
  isUnsubscribingError = false;

  isDiscountOfferModalShown = false;

  // DATA END

  // FORM VALIDATION
  valid = true;
  submitted = false;

  @Watch('selectedCancellationOption')
  async onSelectedCancellationOption(val: string): Promise<void> {
    if (val === 'too_expensive') {
      if (!this.discountInfo) {
        await this.getDiscountCodeInformation();
      }
    }
  }

  discountInfo: DiscountInfo | null = null;
  discountInfoLoading = false;
  async getDiscountCodeInformation(): Promise<void> {
    this.discountInfoLoading = true;
    const response = await PromoCodeService.getDiscountCodeInformation();
    if (response) {
      this.discountInfo = response;
    }
    this.discountInfoLoading = false;
  }

  get passwordRules(): any {
    return [
      (v: string) => {
        return !!v || this.$t('fields.account.billing.cancel_subs.page_field_required');
      },
    ];
  }
  get checkboxesInvalid(): boolean {
    return this.questionFourSelected.length !== this.answerFour.length;
  }

  get validateCancellationReasons(): boolean {
    return !!this.selectedCancellationOption;
  }

  get isSSOUser(): boolean {
    return store.state.auth.providerType !== 'default';
  }

  get currentPeriodEnd(): string {
    return dayjs(this.subscription.current_period_end).format('MMM DD, YYYY');
  }

  // METHODS
  beforeRouteEnter(to: Route, from: Route, next: NavigationGuardNext): void {
    if (to.name === 'cancel_subscription' && from.path !== '/my-account/billing') next({ path: '/my-account/billing' });
    next();
  }

  get isFeatureEng2202Enabled(): boolean {
    return isFeatureEnabled(
      optimizelyFeatureFlags,
      'cancellation-discount-modal',
      store.state?.auth?.user?.id.toString()
    );
  }

  get abTestValue(): string | null {
    return this.isFeatureEng2202Enabled &&
      PromoCodeService.isPartOfABTestENG2250(true) &&
      this.selectedCancellationOption === 'too_expensive'
      ? PromoCodeService.AB_TEST_DISCOUNT_DELIVERY
      : null;
  }

  async onClickCancelSubscription(): Promise<void> {
    this.submitted = true;
    if ((this as any).$refs.form.validate() && !this.checkboxesInvalid && this.validateCancellationReasons) {
      if (
        this.isFeatureEng2202Enabled &&
        PromoCodeService.isPartOfABTestENG2250() &&
        this.selectedCancellationOption === 'too_expensive' &&
        !PromoCodeService.isCouponCodeEnabled()
      ) {
        this.isDiscountOfferModalShown = true;
        this.snowplowTrackCancelSubscription(true);
      } else {
        await this.cancelSubscription(false);
      }
    } else {
      this.snowplowTrackCancelSubscription(false);
    }
  }

  async cancelSubscription(skipTracking = false): Promise<void> {
    this.isUnsubscribing = true;
    this.isDiscountOfferModalShown = false;
    this.stripeSubscriptionService
      .cancel(this.subscription.id)
      .then(async () => {
        if (!skipTracking) {
          this.snowplowTrackCancelSubscription(true);
        }
        await this.cancelSubscriptionSuccess();
        this.isUnsubscribing = false;
      })
      .catch(() => {
        this.isUnsubscribingError = true;
      });
  }

  snowplowTrackCancelSubscription(success: boolean): void {
    const user_id = store.state?.auth?.user?.id;
    const account = store.state?.account?.account;
    let reason_text =
      this.cancellationReasons?.find((item) => item.value === this.selectedCancellationOption)?.label || null;
    if (this.showCounterArguments ? true : this.selectedCancellationOption === 'other') {
      reason_text = this.cancellationOtherReason;
    }
    trackCancelSubscription({
      industry_id: account?.industry_id ?? null,
      user_id,
      reason_identifier: this.selectedCancellationOption,
      reason_text,
      frontend_validation_succeeded: success,
      ab_test: success ? this.abTestValue : null,
    });
  }

  async cancelSubscriptionSuccess(): Promise<void> {
    await store.dispatch('layout/setSnackbar', {
      type: 'info',
      icon: 'info-system',
      text: this.$t('fields.account.billing.cancel_subs.page_subs_ok'),
      isSnackbar: false,
      active: true,
    });
    await this.$router.push({ name: 'my_account_tab', params: { tab: 'billing' } });
  }

  onKeepSubscription(): void {
    this.$router.push({ name: 'my_account_tab', params: { tab: 'billing' } });
  }
  // METHODS END

  // Cancel Subscription reasons
  selectedCancellationOption: SnowplowCancellationReasonIdentifier = '' as SnowplowCancellationReasonIdentifier;
  cancellationOtherReason = '';
  cancellationReasons: CancellationReasonsInterface[] = [
    {
      id: 1,
      label: this.$t('fields.account.billing.cancel_subs.page_option_one') as string,
      value: 'missing_features',
      counterArgumentBanner: {
        backgroundColor: '#FFCDD74D',
        title: this.$t('fields.account.billing.cancel_subs.counter_arguments.page_option_one.title') as string,
        iconName: 'communication',
      },
    },
    {
      id: 2,
      label: this.$t('fields.account.billing.cancel_subs.page_option_two') as string,
      value: 'technical_problems',
      counterArgumentBanner: {
        backgroundColor: '#00BFFF1A',
        title: this.$t('fields.account.billing.cancel_subs.counter_arguments.page_option_two.title') as string,
        iconName: 'support',
        actions: [
          {
            id: 1,
            buttonColor: 'primary',
            buttonCta: this.$t('fields.account.billing.cancel_subs.counter_arguments.page_option_two.button') as string,
            buttonLink: 'https://support.qr-code-generator.com/hc/en-us',
          },
        ],
      },
    },
    {
      id: 3,
      label: this.$t('fields.account.billing.cancel_subs.page_option_three') as string,
      value: 'bad_outcome',
      counterArgumentBanner: {
        backgroundColor: '#FFCDD74D',
        title: this.$t('fields.account.billing.cancel_subs.counter_arguments.page_option_three.title') as string,
        iconName: 'communication',
      },
    },
    {
      id: 4,
      label: this.$t('fields.account.billing.cancel_subs.page_option_four') as string,
      value: 'not_using_enough',
      counterArgumentBanner: {
        backgroundColor: '#00BFFF1A',
        title: this.$t('fields.account.billing.cancel_subs.counter_arguments.page_option_four.title') as string,
        description: this.$t(
          'fields.account.billing.cancel_subs.counter_arguments.page_option_four.description'
        ) as string,
        iconName: 'idea',
        actions: [
          {
            id: 1,
            buttonColor: 'primary',
            buttonCta: this.$t(
              'fields.account.billing.cancel_subs.counter_arguments.page_option_four.button'
            ) as string,
            buttonLink: 'https://www.qr-code-generator.com/blog/category/best-practices/',
          },
        ],
      },
    },
    {
      id: 5,
      label: this.$t('fields.account.billing.cancel_subs.page_option_five') as string,
      value: 'project_finished',
      counterArgumentBanner: {
        backgroundColor: '#FBF3724D',
        title: this.$t('fields.account.billing.cancel_subs.counter_arguments.page_option_five.title') as string,
        description: this.$t(
          'fields.account.billing.cancel_subs.counter_arguments.page_option_five.description'
        ) as string,
        iconName: 'curated-content',
        actions: [
          {
            id: 1,
            buttonColor: 'banana darken-1',
            buttonCta: this.$t(
              'fields.account.billing.cancel_subs.counter_arguments.page_option_five.button'
            ) as string,
            buttonLink: 'https://www.qr-code-generator.com/blog/category/best-practices/',
          },
        ],
      },
    },
    {
      id: 6,
      label: this.$t('fields.account.billing.cancel_subs.page_option_six') as string,
      value: 'too_expensive',
      counterArgumentBanner: {
        backgroundColor: '#00BFFF1A',
        title: this.$t('fields.account.billing.cancel_subs.counter_arguments.page_option_six.title') as string,
        description: this.$t(
          'fields.account.billing.cancel_subs.counter_arguments.page_option_six.description'
        ) as string,
        iconName: 'management',
        actions: [
          {
            id: 1,
            buttonColor: 'primary',
            buttonOutlined: true,
            buttonCta: this.$t(
              'fields.account.billing.cancel_subs.counter_arguments.page_option_six.button_left'
            ) as string,
            buttonLink:
              'https://support.qr-code-generator.com/hc/en-us/articles/7665582564365-How-do-I-pause-a-QR-Code-',
          },
          {
            id: 2,
            buttonColor: 'primary',
            buttonOutlined: true,
            buttonCta: this.$t(
              'fields.account.billing.cancel_subs.counter_arguments.page_option_six.button_right'
            ) as string,
            buttonLink:
              'https://support.qr-code-generator.com/hc/en-us/articles/7665588128909-How-do-I-delete-a-QR-Code-',
          },
        ],
      },
    },
    {
      id: 7,
      label: this.$t('fields.account.billing.cancel_subs.page_option_seven') as string,
      value: 'unsure_how_to_use',
      counterArgumentBanner: {
        backgroundColor: '#00BFFF14',
        title: this.$t('fields.account.billing.cancel_subs.counter_arguments.page_option_seven.title') as string,
        description: this.$t(
          'fields.account.billing.cancel_subs.counter_arguments.page_option_seven.description'
        ) as string,
        iconName: 'frames',
        actions: [
          {
            id: 1,
            buttonColor: 'primary',
            buttonCta: this.$t(
              'fields.account.billing.cancel_subs.counter_arguments.page_option_seven.button_left'
            ) as string,
            buttonLink: 'https://support.qr-code-generator.com/hc/en-us/categories/7662957150221-Getting-Started',
          },
          {
            id: 2,
            buttonColor: 'primary',
            buttonOutlined: true,
            buttonCta: this.$t(
              'fields.account.billing.cancel_subs.counter_arguments.page_option_seven.button_right'
            ) as string,
            buttonLink: 'https://www.qr-code-generator.com/blog/',
          },
        ],
      },
    },
    {
      id: 8,
      label: this.$t('fields.account.billing.cancel_subs.page_option_eight') as string,
      value: 'other',
    },
  ];

  /**
   * If feature-flag is active and user language is english, show counter-argument banners
   */
  get showCounterArguments(): boolean {
    // ToDo: When removing the lang check, we should also remove the feature-flag
    const isFeatureEng2032Enabled = isFeatureEnabled(
      optimizelyFeatureFlags,
      'cancellation_form_counter_arguments',
      store.state?.auth?.user?.id.toString()
    );

    if (!isFeatureEng2032Enabled) return false;

    // ToDo: Remove lang check when translations are available (goal is to roll out the feature to all users)
    const user = store.state?.auth?.user;
    return user.language === 'en';
  }
}
